
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { AnimalProfileService } from 'src/app/features/animal/animal-profile.service';
import { AuthToken } from 'src/app/shared/model/auth-token';
import { Login } from 'src/app/shared/model/login';
import {
  AuthFormsService,
  AuthFormsState,
} from 'src/app/shared/service/login-register-forms.service';
import { User } from '../../../shared/model/user';
import { UserActions } from '../../../state/user.actions';
import { AuthService } from '../auth.service';
import { RegisterComponent } from '../register/register.component';

@Component({
  imports: [FormsModule, RegisterComponent, RouterModule],
  selector: "app-form",
  templateUrl: "./form.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./form.component.css"],
})
export class FormComponent implements OnInit, OnDestroy {
  @Input() resetPassword!: boolean;
  @Output() loginState: EventEmitter<boolean> = new EventEmitter();

  Store = inject(Store);
  router = inject(Router);

  fadeInElement!: HTMLElement;

  // Component state
  public loading: boolean = false;
  public showPassword = false;

  // Error msg fields
  public authFormsState!: AuthFormsState;
  public showErrorMessages!: boolean;
  public inloggedUser!: AuthToken;

  // registration state
  public registrationComplete: boolean = false;
  public registrationEmail = "";

  // login only fields
  public loginData!: Login;
  public loginError: string = "";

  // Subscriptions
  private destroy$ = new Subject<void>();

  constructor(
    private authenticationService: AuthService,
    private animalService: AnimalProfileService,
    private authFormsService: AuthFormsService,
  ) { }

  public ngOnInit(): void {
    this.authFormsService.authFormsStateObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((authFormsState) => {
        this.authFormsState = authFormsState;
      });

    this.loginData = new Login();
    this.loginData.email = "";
    this.loginData.password = "";

    localStorage.removeItem("messageHubId");
    localStorage.removeItem("notificationHubId");
  }

  public ngOnDestroy() {
    this.destroy$.next(); // Terminates any other active subscriptions
    this.destroy$.complete(); // Complete this subject
  }

  public login() {
    this.loginError = "";
    this.authenticationService
      .loginUser(this.loginData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({

        next: async (res: any) => {
          if (res?.isSuccess === false) {
            this.loginError = res.message;
            return;
          }
          this.inloggedUser = res;
          const user = this.inloggedUser.user as User;
          this.Store.dispatch(UserActions.setUser({ newUser: user }));
          this.animalService.getUserAnimals(this.inloggedUser.user.id);

          await this.router.navigate(["/"], {
            onSameUrlNavigation: "reload",
          });
        },

        error: (error: HttpErrorResponse) => {
          const status = error.status || Number(error.message);

          if (status === 401) {
            this.loginError = "Felaktig epostadress eller lösenord.";
            return;
          }
          if (status === 403) {
            this.loginError =
              "Du måste bekräfta din e-postadress innan du kan logga in.";
            return;
          }
          if (status === 500) {
            this.loginError =
              "Ett serverproblem uppstod. Vänligen försök igen vid ett senare tillfälle.";
            return;
          }
          const jsonMessage =
            error?.error?.errors?.[0]?.message || error?.error?.message;
          if (jsonMessage) {
            this.loginError = jsonMessage;
            return;
          }
          this.loginError = "Ett oväntat fel inträffade.";
        },
      });
  }
  public resendLoading: boolean = false;
  public resendSuccess: boolean = false;

  public resendVerificationEmail(): void {
    this.resendLoading = true;
    this.resendSuccess = false;
    this.authenticationService
      .resendConfirmationEmail(this.loginData.email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.resendLoading = false;
          this.resendSuccess = true;
        },
        error: () => {
          this.resendLoading = false;
          this.loginError =
            "Kunde inte skicka verifieringsmejlet. Försök igen.";
        },
      });
  }

  // public checkIfTokenEmpty(): boolean {
  //   return !this.inloggedUser?.token;
  // }

  public isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated;
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public setLoadingState(isLoading: boolean) {
    this.loading = isLoading;
  }

  public setShowErrorMessages(value: boolean) {
    this.showErrorMessages = value;
  }

  public onRememberMe = (value: boolean) => {
    this.loginData.rememberMe = value;
  };

  public showLoginForm() {
    this.registrationComplete = false;
    this.authFormsService.showLoginForm();
  }

  public showRegisterForm() {
    this.registrationComplete = false;
    this.authFormsService.showRegisterForm();
  }

  public userRegistered(obj: any) {
    this.registrationComplete = true;
    this.registrationEmail = obj.email;
    // this.loginData.email = obj.email;
    // this.loginData.password = obj.password;
    this.loginError = "";
    this.router.navigate(["/login/verify-email-sent"], {
      queryParams: { email: obj.email },
    });
  }

  public backToLogin(): void {
    this.registrationComplete = false;
    this.showLoginForm();
  }

  public isEmailValid(email: string): boolean {
    return /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(email);
  }
}
